import { mapActions, mapGetters, mapState } from 'vuex';
import { formMixin, validators } from '@/components/forms/mixin';
import popupPageModeMixin from '@/mixins/popupPageModeMixin';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import userAgentMixin from '@/mixins/userAgent';
import recaptcha from '@/services/recaptcha';

const CAPTCHA_FIELD = 'g-recaptcha-response';

export default {
  mixins: [
    formMixin,
    popupPageModeMixin,
    userAgentMixin,
  ],
  props: {
    email: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      // login attempt counter (for v2 fallback rerenders)
      attempt: 1,
      fieldTypePassword: 'password',
      fields: {
        email: this.email,
        password: '',
        rememberMe: true,
      },
      timeout: null,
      // Pending payload for v2 fallback retry
      pendingPayload: null,
    };
  },
  validations: {
    fields: {
      email: validators.emailNoMessage,
      password: validators.requiredNoMessage,
    },
  },
  computed: {
    ...mapGetters('user', ['isUserRestricted']),
    ...mapState('popup', ['currentPopupOptions']),
    loginButtonDisabled() {
      return this.invalidOrDisabled;
    },
  },
  mounted() {
    // Check if we were redirected here with a login error (e.g., from registration flow)
    if (this.currentPopupOptions && this.currentPopupOptions.loginError) {
      this.formHandleErrors(this.currentPopupOptions.loginError);
      // Clear the error from options so it doesn't show again on re-render
      this.setCurrentPopupOptions({});
    }
  },
  methods: {
    ...mapActions('user', ['login', 'setUseDisqus', 'loginDisqus']),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentPopupOptions']),
    ...mapActions('profile', ['setProfileStatus']),
    showPassword() {
      this.fieldTypePassword = (this.fieldTypePassword === 'password') ? 'text' : 'password';
    },
    onForgotten() {
      if (this.isPage) {
        this.$router.push({ name: 'account-password-forgotten' });
      } else {
        this.setCurrentPopup(MODALS.FORGOTTEN);
      }
    },
    getRedirectPath() {
      if (this.$router.currentRoute.query.redirect) return this.$router.currentRoute.query.redirect;
      if (this.$router.currentRoute.name === 'home' || this.isPage) return { name: 'portfolio' };
      return null;
    },
    triggerRedirect(redirect) {
      this.$router.push(redirect);
      if (this.isIos) {
        this.$nextTick(() => {
          setTimeout(() => {
            window.scrollTo(0, 0);
          }, 100);
        });
      }
    },
    onLogin() {
      this.setProfileStatus({ event: 'login' });
      if (this.$route && this.$route.name === 'disqus-auth') {
        this.loginDisqus()
          .then(() => {
            this.setUseDisqus(true);
            window.close();
          });
      } else {
        const redirect = this.getRedirectPath();
        this.setCurrentPopup();
        if (redirect) {
          this.triggerRedirect(redirect);
        }
      }
    },
    async onSubmit() {
      if (this.formIsValid() && this.formDisable()) {
        try {
          // Step 1: Get invisible v3 token
          const v3Token = await recaptcha.executeV3('login');

          const payload = {
            [CAPTCHA_FIELD]: v3Token,
            'recaptcha-version': 'v3',
            email: this.fields.email,
            password: this.fields.password,
            grant_type: 'password',
            rememberMe: this.fields.rememberMe,
          };

          this.login(payload)
            .then((result) => {
              this.formEnable();

              // Check if 2FA challenge is required
              if (result.requiresTwoFactor) {
                // Switch to 2FA verification modal
                this.setCurrentPopup(MODALS.TWO_FACTOR);
                return;
              }

              this.onLogin();
            })
            .catch((error) => {
              // Step 2: Check if v3 failed and v2 challenge is needed
              if (recaptcha.isV3ScoreFailure(error)) {
                this.pendingPayload = payload;
                this.attempt += 1;
                this.setCurrentPopup(MODALS.RECAPTCHA_CHALLENGE);
                this.setCurrentPopupOptions({
                  attempt: this.attempt,
                  onVerified: this.onRecaptchaV2Verified,
                });
                this.formEnable();
                return;
              }

              this.attempt += 1;
              this.formHandleErrors(error);
            });
        } catch (err) {
          // Error getting v3 token - fall back to showing error
          this.formEnable();
          this.formHandleErrors(err);
        }
      }
    },
    onRecaptchaV2Verified(v2Token) {
      // Called when user completes v2 challenge in modal
      // Don't switch to login modal yet - keep reCAPTCHA visible during API call
      // to avoid "flash" of login form before 2FA or success transition

      const payload = {
        ...this.pendingPayload,
        [CAPTCHA_FIELD]: v2Token,
        'recaptcha-version': 'v2',
      };

      this.login(payload)
        .then((result) => {
          this.formEnable();

          if (result.requiresTwoFactor) {
            this.setCurrentPopup(MODALS.TWO_FACTOR);
            return;
          }

          // Success - close modal and proceed
          this.onLogin();
        })
        .catch((error) => {
          // On error, pass error via popup options so new login modal can display it
          // (calling formHandleErrors here won't work as this component is destroyed)
          this.setCurrentPopup(MODALS.LOGIN);
          this.setCurrentPopupOptions({ loginError: error });
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/account/account-login/account-login.js